type MentionItem = {
  type: string;
  id: string;
  label: string;
  summary: string;
  insert_text: string;
};

type MentionMenuState = {
  trigger: string;
  query: string;
  rangeStart: number;
  rangeEnd: number;
};

type MentionRoute = {
  regex: RegExp;
  type: string;
};

const mentionState = {
  activeTextarea: null as HTMLTextAreaElement | null,
  activeMenu: null as HTMLElement | null,
  activeItems: [] as MentionItem[],
  activeIndex: 0,
  activeToken: null as MentionMenuState | null,
  requestHandle: 0,
};

const UUID_PATTERN = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

const MENTION_ROUTES: MentionRoute[] = [
  { regex: new RegExp(`^/gm/factions/(${UUID_PATTERN})/?$`, "i"), type: "faction" },
  { regex: new RegExp(`^/gm/qrcodes/(${UUID_PATTERN})/?$`, "i"), type: "qr" },
  { regex: new RegExp(`^/gm/participants/(${UUID_PATTERN})/?$`, "i"), type: "user" },
  { regex: new RegExp(`^/gm/quests/(${UUID_PATTERN})/?$`, "i"), type: "quest" },
  { regex: new RegExp(`^/gm/locations/(${UUID_PATTERN})/?$`, "i"), type: "location" },
];

function mentionTextareaSelector(): string {
  return "[data-mention-textarea]";
}

function smartPasteSelector(): string {
  return `${mentionTextareaSelector()}[data-mention-enabled="true"]`;
}

function ensureMentionMenu(): HTMLElement {
  let menu = document.querySelector<HTMLElement>("[data-mention-menu]");
  if (menu) return menu;
  menu = document.createElement("div");
  menu.className = "mention-menu";
  menu.hidden = true;
  menu.dataset.mentionMenu = "true";
  menu.setAttribute("role", "listbox");
  document.body.appendChild(menu);
  return menu;
}

export function mentionTokenFor(textarea: HTMLTextAreaElement): MentionMenuState | null {
  const caret = textarea.selectionStart ?? textarea.value.length;
  const prefix = textarea.value.slice(0, caret);
  const match = /(^|[\s(])([@#])([^\s@#]{0,40})$/.exec(prefix);
  if (!match) return null;
  const trigger = match[2] ?? "";
  if (!trigger) return null;
  const query = (match[3] || "").trim();
  const tokenStart = caret - (trigger.length + query.length);
  if (tokenStart < 0) return null;
  return {
    trigger,
    query,
    rangeStart: tokenStart,
    rangeEnd: caret,
  };
}

function closeMentionMenu(): void {
  if (mentionState.activeMenu) {
    mentionState.activeMenu.hidden = true;
    clearChildren(mentionState.activeMenu);
  }
  mentionState.activeItems = [];
  mentionState.activeIndex = 0;
  mentionState.activeToken = null;
}

function positionMentionMenu(textarea: HTMLTextAreaElement, menu: HTMLElement): void {
  const rect = textarea.getBoundingClientRect();
  menu.style.left = `${window.scrollX + rect.left}px`;
  menu.style.top = `${window.scrollY + rect.bottom + 8}px`;
  menu.style.width = `${Math.min(rect.width, 420)}px`;
}

function dispatchTextareaMutation(textarea: HTMLTextAreaElement): void {
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  textarea.dispatchEvent(new Event("change", { bubbles: true }));
}

function insertTextareaText(textarea: HTMLTextAreaElement, replacement: string): void {
  textarea.focus();
  const documentWithExecCommand = document as Document & {
    execCommand?: (commandId: string, showUI?: boolean, value?: string) => boolean;
  };
  if (typeof documentWithExecCommand.execCommand === "function") {
    try {
      if (documentWithExecCommand.execCommand("insertText", false, replacement)) {
        dispatchTextareaMutation(textarea);
        return;
      }
    } catch {
      // Fall back to setRangeText when the browser blocks execCommand.
    }
  }

  const selectionStart = textarea.selectionStart ?? textarea.value.length;
  const selectionEnd = textarea.selectionEnd ?? selectionStart;
  textarea.setRangeText(replacement, selectionStart, selectionEnd, "end");
  dispatchTextareaMutation(textarea);
}

function isInternalMentionURL(url: URL): boolean {
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }
  const hostname = url.hostname.toLowerCase();
  const currentHost = window.location.hostname.toLowerCase();
  return hostname === currentHost || hostname === "wemeshup.com" || hostname.endsWith(".wemeshup.com");
}

function mentionReplacementForPaste(text: string): string | null {
  const candidate = text.trim();
  if (!candidate) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (!isInternalMentionURL(url)) {
    return null;
  }
  for (const route of MENTION_ROUTES) {
    const match = route.regex.exec(url.pathname);
    if (!match || !match[1]) {
      continue;
    }
    return `!mention[${route.type}:${match[1].toLowerCase()}]`;
  }
  return null;
}

function applyMentionSelection(textarea: HTMLTextAreaElement, item: MentionItem): void {
  const token = mentionState.activeToken;
  if (!token) return;
  const before = textarea.value.slice(0, token.rangeStart);
  const after = textarea.value.slice(token.rangeEnd);
  const insertion = `${item.insert_text} `;
  textarea.value = `${before}${insertion}${after}`;
  const nextPos = before.length + insertion.length;
  textarea.focus();
  textarea.setSelectionRange(nextPos, nextPos);
  dispatchTextareaMutation(textarea);
  closeMentionMenu();
}

function renderMentionMenu(textarea: HTMLTextAreaElement, items: MentionItem[]): void {
  const menu = ensureMentionMenu();
  mentionState.activeMenu = menu;
  mentionState.activeTextarea = textarea;
  mentionState.activeItems = items;
  mentionState.activeIndex = 0;
  clearChildren(menu);
  if (!items.length) {
    closeMentionMenu();
    return;
  }
  items.forEach((item, index) => {
    const button = createElement("button", {
      className: `mention-menu__item${index === 0 ? " is-active" : ""}`,
      attrs: {
        type: "button",
        role: "option",
      },
    });
    const label = createElement("span", {
      className: "mention-menu__label",
      text: item.label,
    });
    const meta = createElement("span", {
      className: "mention-menu__meta",
      text: item.summary || item.type,
    });
    button.append(label, meta);
    button.addEventListener("mousedown", (event) => {
      event.preventDefault();
      applyMentionSelection(textarea, item);
    });
    menu.appendChild(button);
  });
  positionMentionMenu(textarea, menu);
  menu.hidden = false;
}

async function fetchMentionSuggestions(textarea: HTMLTextAreaElement, token: MentionMenuState): Promise<void> {
  const searchURL = String(textarea.getAttribute("data-mention-search-url") || "").trim();
  if (!searchURL) {
    closeMentionMenu();
    return;
  }
  const requestID = ++mentionState.requestHandle;
  const url = new URL(searchURL, window.location.origin);
  url.searchParams.set("trigger", token.trigger);
  url.searchParams.set("q", token.query);
  const response = await fetch(url.toString(), {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  });
  if (!response.ok || requestID != mentionState.requestHandle) {
    return;
  }
  const payload = await response.json() as { items?: MentionItem[] };
  if (requestID != mentionState.requestHandle) {
    return;
  }
  renderMentionMenu(textarea, Array.isArray(payload.items) ? payload.items : []);
}

function moveMentionSelection(delta: number): void {
  if (!mentionState.activeMenu || !mentionState.activeItems.length) return;
  const nextIndex = (mentionState.activeIndex + delta + mentionState.activeItems.length) % mentionState.activeItems.length;
  mentionState.activeIndex = nextIndex;
  mentionState.activeMenu.querySelectorAll<HTMLElement>(".mention-menu__item").forEach((item, index) => {
    item.classList.toggle("is-active", index === nextIndex);
  });
}

function bindMentionTextarea(textarea: HTMLTextAreaElement): void {
  if (textarea.dataset.mentionBound === "true") return;
  textarea.dataset.mentionBound = "true";

  if (textarea.matches(smartPasteSelector())) {
    textarea.addEventListener("paste", (event) => {
      const clipboard = event.clipboardData;
      const replacement = mentionReplacementForPaste(clipboard?.getData("text") || "");
      if (!replacement) {
        return;
      }
      event.preventDefault();
      closeMentionMenu();
      mentionState.activeTextarea = textarea;
      insertTextareaText(textarea, replacement);
    });
  }

  textarea.addEventListener("input", () => {
    const token = mentionTokenFor(textarea);
    mentionState.activeToken = token;
    if (!token) {
      closeMentionMenu();
      return;
    }
    void fetchMentionSuggestions(textarea, token);
  });

  textarea.addEventListener("keydown", (event) => {
    if (!mentionState.activeItems.length || mentionState.activeTextarea !== textarea) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveMentionSelection(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveMentionSelection(-1);
      return;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      const item = mentionState.activeItems[mentionState.activeIndex];
      if (item) {
        applyMentionSelection(textarea, item);
      }
      return;
    }
    if (event.key === "Escape") {
      closeMentionMenu();
    }
  });

  textarea.addEventListener("blur", () => {
    window.setTimeout(() => {
      if (document.activeElement instanceof HTMLElement && document.activeElement.closest("[data-mention-menu]")) {
        return;
      }
      closeMentionMenu();
    }, 120);
  });
}

function bindMentionTextareas(root: ParentNode): void {
  root.querySelectorAll<HTMLTextAreaElement>(mentionTextareaSelector()).forEach(bindMentionTextarea);
}

function initMentionAutocomplete(): void {
  bindMentionTextareas(document);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(mentionTextareaSelector())) {
          bindMentionTextarea(node as HTMLTextAreaElement);
        }
        bindMentionTextareas(node);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.closest("[data-mention-menu]") || target.closest(mentionTextareaSelector())) {
      return;
    }
    closeMentionMenu();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initMentionAutocomplete, { once: true });
} else {
  initMentionAutocomplete();
}
import { clearChildren, createElement } from "./utils/html";
